import React from 'react';
import {motion} from 'framer-motion';
import {Link} from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiArrowUpRight, FiPhone, FiMapPin, FiMousePointer, FiCheck, FiTrendingUp} = FiIcons;

const actions = [
  {icon: FiPhone, value: '396', label: 'Calls'},
  {icon: FiMapPin, value: '492', label: 'Direction requests'},
  {icon: FiMousePointer, value: '536', label: 'Website clicks'}
];

const rankingExamples = [
  {keyword: 'Gel manicure', before: '37.28%', after: '50.89%'},
  {keyword: 'Nail shop', before: '48.52%', after: '55.03%'},
  {keyword: 'Nail salon with walk-ins', before: '36.09%', after: '46.75%'}
];

function ActionCard({item}) {
  return (
    <div className="rounded-2xl border border-white/10 bg-[#22344d] p-5">
      <SafeIcon icon={item.icon} className="mb-4 h-5 w-5 text-[#3fc0fa]" />
      <p className="text-3xl font-black text-white">{item.value}</p>
      <p className="mt-1 text-sm text-slate-300">{item.label}</p>
    </div>
  );
}

function RankingTable() {
  return (
    <div className="overflow-hidden rounded-2xl border border-white/10 bg-[#19283d]">
      <div className="border-b border-white/10 px-5 py-4">
        <h4 className="font-bold text-white">More top-three placements for key searches</h4>
        <p className="mt-1 text-xs text-slate-400">Share of audited map locations ranking in the top three</p>
      </div>
      <div className="divide-y divide-white/10">
        {rankingExamples.map((row) => (
          <div key={row.keyword} className="grid grid-cols-[1fr_auto_auto] items-center gap-3 px-5 py-4 text-sm">
            <span className="font-medium text-white">{row.keyword}</span>
            <span className="text-slate-400">{row.before}</span>
            <span className="font-bold text-[#a7e2be]">→ {row.after}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function NinetiesNailsCaseStudy() {
  return (
    <section aria-labelledby="nineties-nails-title" className="bg-[#0b1423] py-20 text-white sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div initial={{opacity: 0, y: 24}} whileInView={{opacity: 1, y: 0}} viewport={{once: true}} className="overflow-hidden rounded-[2rem] border border-white/10 bg-[#142033]">
          <div className="grid gap-10 p-6 sm:p-10 lg:grid-cols-[1fr_0.9fr] lg:gap-16 lg:p-14">
            <div>
              <p className="mb-5 text-xs font-bold uppercase tracking-[0.2em] text-[#3fc0fa]">Case study · Local beauty</p>
              <h2 id="nineties-nails-title" className="text-4xl font-black leading-tight tracking-tight sm:text-5xl">90&apos;s Nails: a local presence that keeps working.</h2>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-slate-300">
                For this nail salon, consistent publishing across Google, Facebook and Instagram goes hand in hand with measurable customer activity.
              </p>
              <div className="mt-8 rounded-2xl bg-[#3fc0fa] p-6 text-[#0b1423] sm:p-8">
                <p className="text-xs font-bold uppercase tracking-widest">Google Business Profile actions</p>
                <p className="mt-3 text-5xl font-black tracking-tight sm:text-6xl">1,424</p>
                <p className="mt-2 font-semibold">Calls, direction requests and website clicks combined</p>
                <p className="mt-4 border-t border-[#0b1423]/20 pt-4 text-sm">Dashboard-estimated value: <strong>$78,320</strong>. This is an estimate, not verified revenue or a count of unique customers.</p>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2 sm:gap-3">
                {actions.map((item) => <ActionCard key={item.label} item={item} />)}
              </div>
            </div>
            <div className="flex flex-col justify-center gap-5">
              <RankingTable />
              <div className="rounded-2xl border border-white/10 bg-[#19283d] p-6">
                <div className="flex items-center gap-3 text-[#a7e2be]">
                  <SafeIcon icon={FiTrendingUp} className="h-6 w-6" />
                  <h3 className="text-xl font-bold text-white">Progress you can follow</h3>
                </div>
                <p className="mt-4 leading-relaxed text-slate-300">The dashboard’s overall average map ranking moved from 13.7 at baseline to 12.5 in the latest audit. Lower ranking numbers are better.</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-[#19283d] p-6">
                <h3 className="text-xl font-bold">What the ongoing work looks like</h3>
                <ul className="mt-5 space-y-3 text-sm leading-relaxed text-slate-300">
                  {[
                    'Regular posts to Google Business Profile, Facebook and Instagram',
                    'Short videos created and distributed across channels',
                    'Website FAQs generated and progress reports sent'
                  ].map((item) => (
                    <li key={item} className="flex gap-3">
                      <SafeIcon icon={FiCheck} className="mt-1 shrink-0 text-[#3fc0fa]" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-5 border-t border-white/10 bg-[#1b2a40] px-6 py-7 sm:flex-row sm:items-center sm:justify-between sm:px-10 lg:px-14">
            <p className="max-w-xl text-sm leading-relaxed text-slate-300">Want this kind of consistent attention on your own local presence? See what our managed plan includes.</p>
            <Link to="/pricing" className="site-button inline-flex shrink-0 items-center justify-center gap-2 rounded-full px-6 py-3 font-bold">
              Explore Local Momentum <SafeIcon icon={FiArrowUpRight} />
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}